import dayjs from "dayjs";

import { useState } from "react";

import EditNoteIcon from "@mui/icons-material/EditNote";
import SearchIcon from "@mui/icons-material/Search";
import { Drawer, Paper, Typography } from "@mui/material";

import useEvent from "@/hooks/useEvent";
import useCheckCondition from "@/pages/Routes/hooks/useCheckCondition";
import { ConditionType } from "@/pages/Routes/hooks/useCondition";
import { LocationOption } from "@/pages/Routes/hooks/useLocationFilter";

import SearchBar from "../SearchBar";

const getLocationString = (from: LocationOption[], to: LocationOption[]) => {
  if (!from.length || !to.length) return "";

  return `${from.map((map) => map.showName || map.code).join(", ")} - ${to
    .map((map) => map.showName || map.code)
    .join(", ")}`;
};

const getDateString = (startDate: number, endDate?: number) => {
  if (!startDate || !endDate) return "";

  let ret = dayjs(startDate).format("MM/DD/YY");

  if (endDate) {
    ret += " - " + dayjs(endDate).format("MM/DD/YY");
  }

  return ret;
};

interface MobileSearchBarProps {
  isLoading?: boolean;
  data?: ConditionType;
  onSearch: (data: ConditionType, isClick?: boolean) => void;
  useWhere?: "home" | "routes";
}

const MobileSearchBar = ({
  data,
  onSearch,
  isLoading,
  useWhere = "routes",
}: MobileSearchBarProps) => {
  const [openDrawer, setDrawer] = useState(false);
  const { checkCondition } = useCheckCondition();

  const handleSearch = useEvent((data, isClick) => {
    if (data) {
      const errMsg = checkCondition(data);

      if (!errMsg) {
        onSearch && onSearch(data as ConditionType, isClick);
        setDrawer(false);
      }
    }
  });

  return (
    <div className="px-[10px]">
      <Paper
        elevation={4}
        className="mt-[12px] py-[10px] px-[12px] flex items-center !rounded-[16px]"
        onClick={() => setDrawer(true)}
      >
        <SearchIcon sx={{ color: (theme) => theme.palette.text.primary, fontSize: "22px" }} />

        <div className="flex flex-col flex-1 px-[8px] items-center">
          {data ? (
            <>
              <Typography color="textPrimary" variant="body2" className="flex items-center">
                {getLocationString(data.fromLocation, data.toLocation)}
              </Typography>

              <Typography color="textPrimary" variant="body2" className="flex items-center">
                {getDateString(data.startDate, data.endDate)}
              </Typography>
            </>
          ) : null}
        </div>

        <EditNoteIcon />
      </Paper>

      <Drawer
        keepMounted
        anchor="top"
        open={openDrawer}
        PaperProps={{ style: { borderRadius: "0 0 6px 6px" } }}
        onClose={() => setDrawer(false)}
      >
        <div className="relative h-full overflow-hidden px-[10px]">
          <div className="h-full pt-[16px]">
            <SearchBar onSearch={handleSearch} isLoading={isLoading} useWhere={useWhere} />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default MobileSearchBar;
